import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';

import { BehaviorSubject, Observable, of } from 'rxjs';
import {
  catchError,
  map,
  skip,
  take,
  tap
} from 'rxjs/operators';

import { ConfigurationService } from '@sondermind/configuration';
import {
  BaseHTTPService,
  HandleErrorOptions
} from '@sondermind/http';
import { CreditCard } from '@sondermind/utilities/models-credit-card';
import {
  HTTP_ERROR_HANDLER,
  IHttpErrorHandler
} from '@sondermind/utilities/models-http';

import { CreditCardUpdate } from '../models/credit-card-update.interface';

@Injectable({
  providedIn: 'root'
})
export class CreditCardDataService extends BaseHTTPService {
  constructor(
    private configurationService: ConfigurationService,
    private http: HttpClient,
    @Optional()
    @Inject(HTTP_ERROR_HANDLER)
    httpErr: IHttpErrorHandler[],
  ) {
    super(httpErr);
  }

  private store = new BehaviorSubject<CreditCard[]>([]);
  store$ = this.store.asObservable();

  refresh() {
    this.fetch().subscribe((as) => this.store.next(as));
    return this.store$.pipe(skip(1), take(1));
  }

  fetch(opts: Partial<HandleErrorOptions> = {}): Observable<CreditCard[]> {
    const url = `${this.configurationService.env.apiBase}/v2/credit_cards`;
    return this.http
      .get<{ creditCards: CreditCard[]; }>(url)
      .pipe(
        map((res) => res.creditCards),
        catchError(this.handleError.bind(this, opts))
      );
  }

  create(
    nickname: string,
    stripe_card_token: string,
    accountSetupContext: string = null
  ): Observable<{ card: CreditCard | null; statusCode: number; }> {
    const url = `${this.configurationService.env.apiBase}/v2/credit_cards`;
    const body = {
      account_setup_context: accountSetupContext,
      credit_card: {
        nickname, stripe_card_token
      }
    };

    return this.http
      .post<{ creditCard: CreditCard; }>(url, body, { observe: 'response' })
      .pipe(
        map((res) => {
          const card = res.body?.creditCard;
          const statusCode = res.status;

          if (card) this.store.next([...this.store.value, card]);
          return { card, statusCode };
        }),
        catchError((error) => of({ card: null, statusCode: error.status }))
      );
  }

  update(id: number, data: Partial<CreditCardUpdate>, opts: Partial<HandleErrorOptions> = {}): Observable<CreditCard> {
    const url = `${this.configurationService.env.apiBase}/v2/credit_cards/${id}`;
    return this.http
      .patch<{ creditCard: CreditCard; }>(url, { credit_card: data })
      .pipe(
        map((res) => res.creditCard),
        tap((card) => {
          const others = this.store.value.filter((c) => c.id !== id);
          this.store.next([...others, card]);
        }),
        catchError(this.handleError.bind(this, opts))
      );
  }

  destroy(id: number, opts: Partial<HandleErrorOptions> = {}): Observable<void> {
    const url = `${this.configurationService.env.apiBase}/v2/credit_cards/${id}`;
    return this.http
      .delete<{ creditCard: CreditCard; }>(url)
      .pipe(
        map(() => undefined),
        tap(() => {
          this.store.next(this.store.value.filter((c) => c.id !== id));
        }),
        catchError(this.handleError.bind(this, opts))
      );
  }
}
